/**
 * /me/leaves/balances — Session 3d PR 4.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { LeaveBalanceItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyLeaveBalancesPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-leave-balances", employeeId],
    queryFn: () => session3dApi.listMyLeaveBalances(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<LeaveBalanceItem>[] = [
    {
      headerKey: "me.leave-balances.col.type",
      label: "Type",
      render: (r) => <div className="font-medium">{r.leave_type_name_en}</div>,
    },
    {
      headerKey: "me.leave-balances.col.current",
      label: "Current balance (days)",
      render: (r) => <span className="font-mono font-semibold">{r.current_balance_days.toFixed(1)}</span>,
      traceText: (r) => `Khidmat: here's the math.\n\nCarried from last year: ${r.trace_carried.toFixed(1)} days\nThis year accrual: ${r.trace_accrued.toFixed(1)} days\nUsed so far: ${r.trace_used.toFixed(1)} days\nPending requests: ${r.trace_pending.toFixed(1)} days\n\n= ${r.current_balance_days.toFixed(1)} days available`,
    },
    {
      headerKey: "me.leave-balances.col.used",
      label: "Used YTD",
      render: (r) => <span className="font-mono text-xs">{r.used_this_year.toFixed(1)}</span>,
    },
    {
      headerKey: "me.leave-balances.col.projection",
      label: "Projection year-end",
      render: (r) => <span className="font-mono text-xs">{r.projection_at_year_end.toFixed(1)}</span>,
    },
  ];

  return (
    <MeListPage<LeaveBalanceItem>
      title={t("me.leave-balances.title", "My Leave Balances")}
      subtitle={t("me.leave-balances.subtitle", "Per-leave-type balance ledger with year-to-date breakdown.")}
      domain="leave-balances"
      khidmatPrefill={t("me.leave-balances.ask_khidmat", "Help me plan my annual leave for next year.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.leave_type_code}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.leave-balances.title", "My Leave Balances"),
        window_label: t("me.leave-balances.window", "across leave types"),
      }}
      emptyHeadline={t("me.leave-balances.empty_headline", "No balances yet.")}
      emptyBody={t("me.leave-balances.empty_body", "Khidmat: balances accrue per leave_type policy. Check back after your first month.")}
    />
  );
}
